<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'SendMails',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Array,
      required: true,
      default: null,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const selectedTemplate = ref(null);
  const templates = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    selectedTemplate: selectedTemplate.value,
    templates: templates.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_templates = async () => {
    return $fn.value
      .fetch('list_template')
      .then((data) => _.filter(data, { template_type: 'mjml' }));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedTemplate' },
      { name: 'templates' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { templates: $$load_templates },
    ux: {},
    dataRefs: {
      selectedTemplate,
      templates,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Send emails') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0q5hlk1p1_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_register_nkw95kxcg_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_1040167445267876_notification_7v80nyv83_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.ngettext(
        'One email has been successfully enqueued.',
        '${n} emails have been successfully enqueued.',
        $event.data.resultLength
      ),
    };
  };
  const act_1040167445267876_notification_7v80nyv83_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_1040167445267876_reloadSlickgrid_uo5ak07d6_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_1040167445267876_closeComponent_brw0wtwx7_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_1040167445267876_request_d3hai6ut5_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_a_job',
      data: _.chain(props.items)
        .filter((item) => item.customer_email)
        .map((item) => ({
          job_type: 'sendmail:dunning_letter',
          job_data: {
            id: item.id,
            template: selectedTemplate.value,
            mail_to: item.customer_email,
          },
        }))
        .value(),
      parameters: {
        job_type: 'sendmail:dunning_letter',
      },
    };
  };

  const act_7315092382398562_closeModal_0q5hlk1p1 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0q5hlk1p1_getActionArgs,
    });
  const inst_evh_7315092382398562_close_2wn9u7t0z =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0q5hlk1p1,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_2wn9u7t0z],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_sltq2qrno =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_22j3d8cxl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_sltq2qrno,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_22j3d8cxl],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_g04tigprd =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_gcno729ww =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_g04tigprd,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_gcno729ww],
    event: 'close',
  });
  const act_1040167445267876_register_nkw95kxcg =
    new actionTypes.RegisterAction({
      actionArgs: act_1040167445267876_register_nkw95kxcg_getActionArgs,
    });
  const inst_evh_1040167445267876_success_iq6xclj06 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_register_nkw95kxcg,
      event: 'success',
      displayName: 'register',
    });
  const act_1040167445267876_notification_7v80nyv83 =
    new actionTypes.NotificationAction({
      actionArgs: act_1040167445267876_notification_7v80nyv83_getActionArgs,
      when: act_1040167445267876_notification_7v80nyv83_getWhen,
    });
  const inst_evh_1040167445267876_success_shwcd6n3i =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_notification_7v80nyv83,
      event: 'success',
      displayName: 'notification',
    });
  const act_1040167445267876_reloadSlickgrid_uo5ak07d6 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_uo5ak07d6_getActionArgs,
    });
  const inst_evh_1040167445267876_success_3v7h2acej =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_uo5ak07d6,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_closeComponent_brw0wtwx7 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_brw0wtwx7_getActionArgs,
    });
  const inst_evh_1040167445267876_success_19q4zuetv =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_brw0wtwx7,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_request_d3hai6ut5 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_d3hai6ut5_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_iq6xclj06,
      inst_evh_1040167445267876_success_shwcd6n3i,
      inst_evh_1040167445267876_success_3v7h2acej,
      inst_evh_1040167445267876_success_19q4zuetv,
    ],
  });
  const inst_evh_1040167445267876_submit_jd04c7g41 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_d3hai6ut5,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_jd04c7g41],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_nymq0ewzt =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_m2p2dv08i =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_nymq0ewzt,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_m2p2dv08i],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_2wn9u7t0z,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_22j3d8cxl,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_gcno729ww,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_iq6xclj06,
    inst_evh_1040167445267876_success_shwcd6n3i,
    inst_evh_1040167445267876_success_3v7h2acej,
    inst_evh_1040167445267876_success_19q4zuetv,
    inst_evh_1040167445267876_submit_jd04c7g41,
    submit: inst_evh_1040167445267876_submit_jd04c7g41,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_m2p2dv08i,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_2wn9u7t0z = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_2wn9u7t0z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_22j3d8cxl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_22j3d8cxl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_gcno729ww = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_gcno729ww.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_iq6xclj06 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_iq6xclj06.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_shwcd6n3i = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_shwcd6n3i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_3v7h2acej = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_3v7h2acej.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_19q4zuetv = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_19q4zuetv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_jd04c7g41 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_jd04c7g41.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_m2p2dv08i = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_m2p2dv08i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="SendMails"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="sendmail-options"
        id="zkhcfceubc"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="template"
          :label="gettext('Template')"
          :description="gettext('The template to use to generate the emails.')"
        >
          <ItpFormSelect2
            name="name"
            size="md"
            v-model="selectedTemplate"
            :value="selectedTemplate"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (selectedTemplate =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :options="templates"
            valueField="id"
            textField="name"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          form="zkhcfceubc"
          :text="gettext('Send')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
